// src/pages/Enseignants/ToDoList/AddTaskModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import Button from "../../../components/Button/Button";
import { useUser } from "../../Home/UserContext";
import { addTask } from "../../../services/tachesServices";
import { getAllUsers } from "../../../services/userServices";
import { sendNotification } from "../../../services/notificationServices";

/* --- Quill toolbar identique à AddRequestModal --- */
const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const quillFormats = [
  "header","font","size","bold","italic","underline","strike","blockquote",
  "color","background","script","list","bullet","indent","link","image","video",
];

const isQuillEmpty = (html) => {
  if (!html) return true;
  const text = html.replace(/<(.|\n)*?>/g, "").trim();
  return html === "<p><br></p>" || text.length === 0;
};

const STATUS_OPTIONS = ["A faire", "En cours", "Urgent", "Effectué"];

const defaultForm = () => ({
  categorie: "",
  titre: "",
  contenu: "",
  user_id: "",
  statut: "A faire",
});

export default function AddTaskModal({ isOpen, onClose, refreshList }) {
  const { user } = useUser();

  const [form, setForm] = useState(defaultForm());
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Réinitialise le formulaire à l’ouverture
  useEffect(() => {
    if (isOpen) setForm(defaultForm());
  }, [isOpen]);

  // Fermer via ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Charge uniquement à l’ouverture + filtre Admin/Enseignant (role_id 1 ou 3)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoadingUsers(true);
        const list = await getAllUsers();
        const filtered = (Array.isArray(list) ? list : []).filter(
          (u) => u.role_id === 1 || u.role_id === 3
        );
        // tri alphabétique
        filtered.sort((a, b) =>
          `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
            `${b.nom || ""} ${b.prenom || ""}`,
            "fr"
          )
        );
        setUsers(filtered);
      } catch (e) {
        console.error("Erreur chargement utilisateurs :", e);
        toast.error("Erreur lors du chargement des utilisateurs.");
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.categorie.trim()) return toast.warn("La catégorie est requise.");
      if (!form.titre.trim()) return toast.warn("Le titre est requis.");
      if (isQuillEmpty(form.contenu)) return toast.warn("La description est requise.");
      if (!form.user_id) return toast.warn("Sélectionnez un utilisateur.");
      if (!form.statut) return toast.warn("Sélectionnez un statut.");

      setSubmitting(true);
      try {
        const payload = {
          categorie: form.categorie.trim(),
          titre: form.titre.trim(),
          contenu: form.contenu,           // HTML Quill
          user_id: Number(form.user_id),
          statut: form.statut,
        };

        await addTask(payload);

        // Notification à l’assigné
        try {
          const msg = `${user?.nom || ""} ${user?.prenom || ""} vous a attribué une nouvelle tâche : ${form.titre}`;
          await sendNotification(msg, payload.user_id, user?.token);
        } catch (e) {
          // On ne bloque pas si la notif échoue
          console.warn("Notification non envoyée :", e);
        }

        toast.success("Tâche ajoutée avec succès !");
        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de l'ajout de la tâche :", error);
        toast.error("Erreur lors de l'ajout de la tâche.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, onClose, refreshList, user]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Bandeau dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Ajouter une tâche
          </h3>
          <p className="mt-1 text-white/90 text-xs text-center">
            Renseignez le titre, la catégorie, la description puis assignez la tâche.
          </p>
        </div>

        {/* Formulaire – scrollable sur mobile */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 max-h-[75vh] overflow-auto">
          {/* Ligne Catégorie / Titre */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Catégorie
              </label>
              <input
                type="text"
                value={form.categorie}
                onChange={(e) => setForm((f) => ({ ...f, categorie: e.target.value }))}
                placeholder="Ex : Cours, Communication, Matériel…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Titre
              </label>
              <input
                type="text"
                value={form.titre}
                onChange={(e) => setForm((f) => ({ ...f, titre: e.target.value }))}
                placeholder="Ex : Photos d’adhérents"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
                disabled={submitting}
              />
            </div>
          </div>

          {/* Description (Quill) */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Description
            </label>
            <div className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div className="overflow-x-auto">
                <ReactQuill
                  theme="snow"
                  value={form.contenu}
                  onChange={(val) => setForm((f) => ({ ...f, contenu: val }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Détaillez la tâche, ajoutez des précisions/utiles, liens, etc."
                  className="min-h-[180px]"
                  readOnly={submitting}
                />
              </div>
            </div>
          </div>

          {/* Ligne Assignation / Statut */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Utilisateur assigné
              </label>
              <select
                value={form.user_id}
                onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
                disabled={loadingUsers || submitting}
              >
                <option value="">{loadingUsers ? "Chargement…" : "Sélectionner"}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {(u.nom || "").toUpperCase()} {u.prenom || ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Statut
              </label>
              <select
                value={form.statut}
                onChange={(e) => setForm((f) => ({ ...f, statut: e.target.value }))}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
                disabled={submitting}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              disabled={submitting}
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
